import React from 'react';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/hooks/useAppSelector';

interface FilterSummaryProps {
  compact?: boolean;
}

const FilterSummary: React.FC<FilterSummaryProps> = ({ compact = false }) => {
  // Get filter state from Redux
  const selectedCategories = useAppSelector(state => state.companiesFilter.selectedCategories) || [];
  const selectedEmployees = useAppSelector(state => state.companiesFilter.selectedEmployees) || [];
  const selectedProductCount = useAppSelector(state => state.companiesFilter.selectedProductCount) || [];
  const selectedModalities = useAppSelector(state => state.companiesFilter.selectedModalities) || [];
  const selectedTags = useAppSelector(state => state.companiesFilter.selectedTags) || [];
  
  // Check if any filters are active
  const hasActiveFilters = 
    (selectedCategories.length > 0 && !selectedCategories.includes('All')) ||
    (selectedEmployees.length > 0 && !selectedEmployees.includes('All')) ||
    (selectedProductCount.length > 0 && !selectedProductCount.includes('All')) ||
    (selectedModalities.length > 0 && !selectedModalities.includes('All')) ||
    (selectedTags.length > 0 && !selectedTags.includes('All'));
  
  if (!hasActiveFilters) {
    return null;
  }
  
  // Render active filters as badges
  const renderFilterBadges = () => {
    const badges = [];
    
    // Category filters
    if (selectedCategories.length > 0 && !selectedCategories.includes('All')) {
      selectedCategories.forEach(category => {
        badges.push(
          <Badge 
            key={`category-${category}`} 
            variant="secondary"
            className="bg-blue-50 text-blue-700"
          >
            {!compact && <span className="text-xs font-normal text-blue-500 mr-1">Category:</span>}
            {category}
          </Badge>
        );
      });
    }
    
    // Employee filters
    if (selectedEmployees.length > 0 && !selectedEmployees.includes('All')) {
      selectedEmployees.forEach(size => {
        badges.push(
          <Badge 
            key={`employee-${size}`} 
            variant="secondary"
            className="bg-green-50 text-green-700"
          >
            {!compact && <span className="text-xs font-normal text-green-500 mr-1">Size:</span>}
            {size}
          </Badge>
        );
      });
    }
    
    // Product count filters
    if (selectedProductCount.length > 0 && !selectedProductCount.includes('All')) {
      selectedProductCount.forEach(count => {
        badges.push(
          <Badge 
            key={`product-${count}`} 
            variant="secondary"
            className="bg-purple-50 text-purple-700"
          >
            {!compact && <span className="text-xs font-normal text-purple-500 mr-1">Products:</span>}
            {count}
          </Badge>
        );
      });
    }
    
    // Modality filters
    if (selectedModalities.length > 0 && !selectedModalities.includes('All')) {
      selectedModalities.forEach(modality => {
        badges.push(
          <Badge 
            key={`modality-${modality}`} 
            variant="secondary"
            className="bg-orange-50 text-orange-700"
          >
            {!compact && <span className="text-xs font-normal text-orange-500 mr-1">Modality:</span>}
            {modality}
          </Badge>
        );
      });
    }
    
    // Tag filters
    if (selectedTags.length > 0 && !selectedTags.includes('All')) {
      selectedTags.forEach(tag => {
        badges.push(
          <Badge 
            key={`tag-${tag}`} 
            variant="secondary"
            className="bg-red-50 text-red-700"
          >
            {!compact && <span className="text-xs font-normal text-red-500 mr-1">Tag:</span>}
            {tag}
          </Badge>
        );
      });
    }
    
    return badges;
  };
  
  return (
    <div className={`${compact ? 'mb-2' : 'mb-4'}`}>
      {!compact && (
        <h3 className="text-sm font-medium text-gray-700 mb-2">Active Filters</h3>
      )}
      <div className="flex flex-wrap gap-2">
        {renderFilterBadges()}
      </div>
    </div>
  );
};

export default FilterSummary;
